import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { classNames } from "primereact/utils";
import styles from "../WorkoutPlanDialog.module.scss";
import {
  MuscleGroup,
  MUSCLE_GROUPS_BY_CATEGORY,
  muscleGroupLabel,
} from "../models/muscleGroups";

interface IMuscleGroupPickerProps {
  isOpen: boolean;
  current: MuscleGroup[];
  onClose: () => void;
  /** Called with the full desired set when the sheet closes (server replaces to match). */
  onSave: (groups: MuscleGroup[]) => void;
}

const sameSet = (a: MuscleGroup[], b: MuscleGroup[]): boolean =>
  a.length === b.length && a.every((g) => b.includes(g));

/**
 * Multi-select bottom sheet for tagging an exercise with the muscle groups it
 * targets. Selections are grouped under their high-level category. The chosen set
 * is committed on close (the server replaces the exercise's tags to match), so a
 * single tap can't fire a request per toggle.
 */
export const MuscleGroupPicker = ({
  isOpen,
  current,
  onClose,
  onSave,
}: IMuscleGroupPickerProps) => {
  const [selected, setSelected] = useState<MuscleGroup[]>(current);

  // Re-seed from the live tags each time the sheet opens.
  useEffect(() => {
    if (isOpen) setSelected(current);
  }, [isOpen, current]);

  const commitAndClose = () => {
    if (!sameSet(selected, current)) onSave(selected);
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") commitAndClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selected, current]);

  const toggle = (group: MuscleGroup) => {
    setSelected((prev) =>
      prev.includes(group) ? prev.filter((g) => g !== group) : [...prev, group],
    );
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles.rest_picker_backdrop}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          onClick={(e) => {
            if (e.target === e.currentTarget) commitAndClose();
          }}
        >
          <motion.div
            className={styles.rest_picker_sheet}
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            role="dialog"
            aria-label="Мускулни групи"
          >
            <div className={styles.rest_picker_title}>Мускулни групи</div>
            <div className={styles.rest_picker_sub}>
              Маркирай кои мускули натоварва упражнението. Използва се за
              разпределението по мускулни групи на тренировката.
            </div>

            <div className={styles.muscle_picker_scroll}>
              {MUSCLE_GROUPS_BY_CATEGORY.map((section) => (
                <div key={section.category} className={styles.muscle_picker_section}>
                  <div className={styles.muscle_picker_section_title}>
                    {section.label}
                  </div>
                  <div className={styles.muscle_picker_options}>
                    {section.groups.map((group) => (
                      <button
                        key={group}
                        type="button"
                        className={classNames(styles.muscle_picker_option, {
                          [styles.muscle_picker_option_active]:
                            selected.includes(group),
                        })}
                        onClick={() => toggle(group)}
                      >
                        {muscleGroupLabel(group)}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.muscle_picker_footer}>
              <span className={styles.muscle_picker_count}>
                Избрани: {selected.length}
              </span>
              <button
                type="button"
                className={styles.muscle_picker_done}
                onClick={commitAndClose}
              >
                Готово
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};
